import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import api from '../api';
import OfferDeskNav from '../components/OfferDeskNav';
import {
  applyModeLabel,
  applyModeStyle,
  portalLabel,
  resolveSupportTierKey,
  supportTierCompact,
  supportTierStyle,
  certifiedCoverage,
} from '../utils/applyDisclosure';
import ApplyFlowModal from '../components/ApplyFlowModal';
import { useAuth } from '../context/AuthContext';


/* ─── Icons ──────────────────────────────────────────────── */
const Icons = {
  graduationCap: (c='#94A3B8') => <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke={c} strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M12 14l9-5-9-5-9 5 9 5z"/><path strokeLinecap="round" strokeLinejoin="round" d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z"/></svg>,
  book:          (c='#94A3B8') => <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke={c} strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/></svg>,
  users:         (c='#94A3B8') => <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke={c} strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/></svg>,
  globe:         (c='#94A3B8') => <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke={c} strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>,
  passport:      (c='#94A3B8') => <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke={c} strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M3 14h18M10.5 3L9 21M13.5 3L15 21M3 6a3 3 0 013-3h12a3 3 0 013 3v12a3 3 0 01-3 3H6a3 3 0 01-3-3V6z"/></svg>,
  trendingUp:    (c='#94A3B8') => <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke={c} strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/></svg>,
  code:          (c='#94A3B8') => <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke={c} strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"/></svg>,
  star:          (c='#94A3B8') => <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke={c} strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"/></svg>,
  checkList:     (c='#94A3B8') => <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke={c} strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/></svg>,
  lightBulb:     (c='#4E6C7E') => <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke={c} strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/></svg>,
  location:      (c='#94A3B8') => <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke={c} strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg>,
  clock:         (c='#94A3B8') => <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke={c} strokeWidth={1.8}><circle cx="12" cy="12" r="10"/><path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2"/></svg>,
  calendar:      (c='#94A3B8') => <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke={c} strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>,
  arrowLeft:     () => <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/></svg>,
  arrowUpRight:  () => <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/></svg>,
  sparkle:       () => <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"/></svg>,
  send:          () => <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/></svg>,
  check:         () => <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>,
  bookmark:      () => <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"/></svg>,
  download:      () => <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>,
  linkedin:      () => <svg width="13" height="13" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>,
  remote:        (c='#065F46') => <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke={c} strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>,
  international: (c='#4338CA') => <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke={c} strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>,
  steps: (n) => (
    <div style={{ width:22, height:22, borderRadius:'50%', background:'var(--tandm-gradient-cta)', color:'#fff', fontSize:11, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>{n}</div>
  ),
};

/* ─── Category config ─────────────────────────────────────── */
const CAT_COLORS = {
  'Investment Banking': { bg:'#EEF2FF', color:'#4338CA' },
  'M&A':                { bg:'#FFF1F2', color:'#BE123C' },
  'Private Equity':     { bg:'#F5F3FF', color:'#7C3AED' },
  'Consulting':         { bg:'#ECFDF5', color:'#065F46' },
  'Asset Management':   { bg:'#FFFBEB', color:'#92400E' },
  'Corporate Banking':  { bg:'#EFF6FF', color:'#1D4ED8' },
  'Fintech':            { bg:'#E8F1F3', color:'#4E6C7E' },
  'Hedge Fund':         { bg:'#F0FDFA', color:'#0F766E' },
  'Markets':            { bg:'#ECFEFF', color:'#0891B2' },
  'Other':              { bg:'#EEF2F4', color:'#475569' },
};

/* ─── FadeIn ─────────────────────────────────────────────── */
function FadeIn({ children, delay = 0, y = 20 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}>
      {children}
    </motion.div>
  );
}

/* ─── Glass Card ─────────────────────────────────────────── */
function Card({ children, style, delay = 0 }) {
  return (
    <FadeIn delay={delay}>
      <motion.div
        whileHover={{ y: -2 }}
        transition={{ type: 'spring', stiffness: 300, damping: 20 }}
        style={{
          background: 'rgba(255,255,255,0.78)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,255,255,0.95)',
          borderRadius: 20,
          padding: '24px',
          boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
          ...style,
        }}
      >
        {children}
      </motion.div>
    </FadeIn>
  );
}

/* ─── AI Badge ───────────────────────────────────────────── */
const AIBadge = ({ text = 'AI' }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 4,
    fontSize: 10, fontWeight: 700,
    background: 'var(--tandm-gradient-cta)',
    color: '#fff', padding: '3px 9px', borderRadius: 100,
    textTransform: 'uppercase', letterSpacing: '0.05em',
  }}>
    <Icons.sparkle /> {text}
  </span>
);

/* ─── AI Notes Panel ─────────────────────────────────────── */
const AINotesPanel = ({ notes }) => {
  if (!notes) return null;
  let n;
  try { n = typeof notes === 'string' ? JSON.parse(notes) : notes; } catch { return null; }
  if (!n) return null;

  const infoRows = [
    { icon: Icons.graduationCap, label: 'Year of study',          value: n.year_of_study },
    { icon: Icons.book,          label: 'Degree preference',      value: n.degree_preference },
    { icon: Icons.users,         label: 'Level',                  value: n.level },
    { icon: Icons.passport,      label: 'Visa sponsorship',       value: n.visa_sponsorship },
    { icon: Icons.globe,         label: 'Open to internationals', value: n.open_to_internationals },
  ];

  const chipSections = [
    { icon: Icons.trendingUp, label: 'Financial skills', items: n.financial_skills, color: '#92400E', bg: '#FFFBEB' },
    { icon: Icons.code,       label: 'Technical skills', items: n.technical_skills, color: '#4338CA', bg: '#EEF2FF' },
    { icon: Icons.star,       label: 'Soft skills',      items: n.soft_skills,      color: '#065F46', bg: '#ECFDF5' },
  ];

  return (
    <Card delay={0.1}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, color: '#0B1220', margin: 0, letterSpacing: '-0.02em' }}>Before you apply</h2>
        <AIBadge />
      </div>

      <div style={{ marginBottom: 20 }}>
        {infoRows.map(({ icon: Ic, label, value }) => {
          if (!value || value === 'Not specified') return null;
          return (
            <motion.div
              key={label}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '10px 0', borderBottom: '1px solid rgba(0,0,0,0.05)' }}
            >
              <div style={{ marginTop: 2, flexShrink: 0 }}><Ic /></div>
              <div>
                <p style={{ fontSize: 10, color: '#94A3B8', margin: '0 0 2px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</p>
                <p style={{ fontSize: 13, color: '#3D2E24', margin: 0, fontWeight: 500 }}>{value}</p>
              </div>
            </motion.div>
          );
        })}
      </div>

      {chipSections.map(({ icon: Ic, label, items, color, bg }) => {
        if (!items?.length) return null;
        return (
          <div key={label} style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              <Ic color={color} />
              <p style={{ fontSize: 10, color: '#94A3B8', margin: 0, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</p>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {items.map((item, i) => (
                <motion.span
                  key={i}
                  initial={{ opacity: 0, scale: 0.85 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.04 }}
                  style={{ fontSize: 11, fontWeight: 500, padding: '4px 10px', borderRadius: 8, background: bg, color, fontFamily: 'var(--tandm-font-sans)' }}
                >
                  {item}
                </motion.span>
              ))}
            </div>
          </div>
        );
      })}

      {n.key_requirements?.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
            {Icons.checkList('#475569')}
            <p style={{ fontSize: 10, color: '#94A3B8', margin: 0, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Key requirements</p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {n.key_requirements.map((req, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#4E6C7E', flexShrink: 0, marginTop: 6 }} />
                <p style={{ fontSize: 13, color: '#3D2E24', margin: 0, lineHeight: 1.55 }}>{req}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {n.tips && n.tips !== 'Not specified' && (
        <div style={{ background: 'linear-gradient(135deg,#FFF8F4,#FFF4EE)', border: '1px solid #EDE0D6', borderRadius: 12, padding: '14px 16px', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <div style={{ marginTop: 1, flexShrink: 0 }}><Icons.lightBulb /></div>
          <div>
            <p style={{ fontSize: 10, color: '#4E6C7E', fontWeight: 700, margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Tip</p>
            <p style={{ fontSize: 13, color: '#3D2E24', margin: 0, lineHeight: 1.6 }}>{n.tips}</p>
          </div>
        </div>
      )}
    </Card>
  );
};

/* ─── Save Button ────────────────────────────────────────── */
function SaveButton({ internshipId }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/applications/status/${internshipId}`)
      .then(res => setStatus(res.data.application?.status || null))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [internshipId]);

  const handleSave = async () => {
    try {
      if (status) {
        const appsRes = await api.get('/applications/my');
        const app = appsRes.data.applications.find(a => a.internship_id === parseInt(internshipId));
        if (app) await api.delete(`/applications/${app.id}`);
        setStatus(null);
      } else {
        await api.post('/applications', { internship_id: internshipId, status: 'saved' });
        setStatus('saved');
      }
    } catch (err) { console.error(err); }
  };

  if (loading) return null;
  return (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.97 }}
      onClick={handleSave}
      style={{
        width: '100%', padding: '12px', borderRadius: 12, fontFamily: 'var(--tandm-font-sans)',
        fontSize: 14, fontWeight: 600, cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        border: status ? '1.5px solid #10B981' : '1.5px solid #EDE8E3',
        background: status ? '#ECFDF5' : 'rgba(255,255,255,0.8)',
        color: status ? '#065F46' : '#475569',
        backdropFilter: 'blur(10px)',
        transition: 'all 0.2s',
      }}
    >
      {status ? <><Icons.check /> Saved</> : <><Icons.bookmark /> Save to my list</>}
    </motion.button>
  );
}

/* ─── Cover Letter Generator ─────────────────────────────── */
function CoverLetterGenerator({ internship }) {
  const [step, setStep] = useState('idle');
  const [content, setContent] = useState('');
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(null);
  const wordCount = content.trim().split(/\s+/).filter(Boolean).length;

  if (internship?.cover_letter_required === false) {
    return (
      <Card delay={0.11}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10, background: '#FEF3C7',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#92400E" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
          </div>
          <div>
            <p style={{ fontSize: 14, fontWeight: 700, color: '#92400E', margin: '0 0 4px' }}>No cover letter required</p>
            <p style={{ fontSize: 12.5, color: '#64748B', margin: 0, lineHeight: 1.55 }}>
              This role does not accept a cover letter. Do not attach one — it will not be uploaded.
            </p>
          </div>
        </div>
      </Card>
    );
  }

  const handleGenerate = async () => {
    setStep('generating'); setError('');
    try {
      const res = await api.post('/ai/generate-cover-letter', { internship_id: internship.id, questions: [] });
      setContent(res.data.content); setStep('done');
    } catch (err) { setError(err.response?.data?.error || 'Failed to generate.'); setStep('idle'); }
  };

  const handleDownload = async (type) => {
    setDownloading(type);
    try {
      const res = await api.post(
        type === 'pdf' ? '/ai/download-pdf' : '/ai/download-word',
        { content, internship_id: internship.id },
        { responseType: 'blob' }
      );
      const ext = type === 'pdf' ? 'pdf' : 'docx';
      const mime = type === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement('a');
      a.href = url; a.download = `${internship.company_name}_CoverLetter.${ext}`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { setError('Download failed.'); }
    finally { setDownloading(null); }
  };

  return (
    <Card delay={0.15}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, color: '#0B1220', margin: 0, letterSpacing: '-0.02em' }}>Cover letter</h2>
        <AIBadge text="AI powered" />
      </div>
      <p style={{ fontSize: 13, color: '#64748B', marginBottom: 20, lineHeight: 1.55 }}>
        Tandm writes a tailored cover letter using your CV and this role. Edit it, then download.
      </p>
      {error && (
        <div style={{ background: '#FFF0F0', color: '#D94040', fontSize: 13, borderRadius: 10, padding: '10px 14px', marginBottom: 16 }}>{error}</div>
      )}
      <AnimatePresence mode="wait">
        {step === 'idle' && (
          <motion.button
            key="idle"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            whileHover={{ scale: 1.02, boxShadow: '0 6px 24px rgba(196,98,45,0.3)' }}
            whileTap={{ scale: 0.97 }}
            onClick={handleGenerate}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: 'var(--tandm-gradient-cta)', color: '#fff',
              border: 'none', borderRadius: 12, padding: '12px 22px',
              fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--tandm-font-sans)',
              boxShadow: '0 4px 16px rgba(196,98,45,0.2)',
            }}
          >
            <Icons.sparkle /> Generate cover letter
          </motion.button>
        )}

        {step === 'generating' && (
          <motion.div
            key="generating"
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            style={{ textAlign: 'center', padding: '44px 0' }}
          >
            <div style={{
              width: 48, height: 48, border: '3px solid #EEF2F4', borderTopColor: '#4E6C7E',
              borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 18px',
            }} />
            <p style={{ fontSize: 14, fontWeight: 600, color: '#0B1220', marginBottom: 4 }}>Writing your cover letter...</p>
            <p style={{ fontSize: 12, color: '#94A3B8' }}>Researching {internship.company_name} and reading your CV</p>
          </motion.div>
        )}

        {step === 'done' && (
          <motion.div
            key="done"
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
          >
            <textarea
              value={content}
              onChange={e => setContent(e.target.value)}
              rows={13}
              style={{
                width: '100%', padding: '14px 16px', borderRadius: 12, fontFamily: 'var(--tandm-font-sans)',
                fontSize: 13, color: '#0B1220', background: 'rgba(250,250,248,0.9)',
                border: '1px solid rgba(0,0,0,0.08)', outline: 'none', resize: 'vertical', lineHeight: 1.7,
              }}
            />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 12, color: '#94A3B8' }}>{wordCount} words</span>
              <button onClick={handleGenerate} style={{ fontSize: 12, color: '#94A3B8', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', fontFamily: 'var(--tandm-font-sans)' }}>Regenerate</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {[
                { type: 'pdf', label: 'Download PDF', accent: false },
                { type: 'word', label: 'Download Word', accent: true },
              ].map(({ type, label, accent }) => (
                <motion.button
                  key={type}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => handleDownload(type)}
                  disabled={downloading === type}
                  style={{
                    padding: '11px', fontSize: 13, fontWeight: 600, borderRadius: 10,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    cursor: 'pointer', fontFamily: 'var(--tandm-font-sans)', border: 'none',
                    background: accent ? 'var(--tandm-gradient-cta)' : 'rgba(0,0,0,0.06)',
                    color: accent ? '#fff' : '#475569',
                    boxShadow: accent ? '0 4px 12px rgba(196,98,45,0.2)' : 'none',
                  }}
                >
                  <Icons.download /> {downloading === type ? 'Downloading...' : label}
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}

/* ─── Networking ─────────────────────────────────────────── */
function NetworkingContacts({ companyId, companyName }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingFor, setGeneratingFor] = useState(null);
  const [messages, setMessages] = useState({});
  const [copiedFor, setCopiedFor] = useState(null);

  useEffect(() => {
    api.get(`/contacts/company/${companyId}`)
      .then(res => setContacts(res.data.contacts))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [companyId]);

  const handleGenerateMessage = async (contactId) => {
    setGeneratingFor(contactId);
    try {
      const res = await api.post('/ai/generate-message', { contact_id: contactId });
      setMessages(m => ({ ...m, [contactId]: res.data.message }));
    } catch (err) { console.error(err); }
    finally { setGeneratingFor(null); }
  };

  const handleCopy = (contactId) => {
    navigator.clipboard.writeText(messages[contactId]);
    setCopiedFor(contactId);
    setTimeout(() => setCopiedFor(null), 2000);
  };

  if (loading || contacts.length === 0) return null;

  return (
    <Card delay={0.2}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, color: '#0B1220', margin: 0, letterSpacing: '-0.02em' }}>Networking contacts</h2>
        <AIBadge text="AI powered" />
      </div>
      <p style={{ fontSize: 13, color: '#64748B', marginBottom: 20 }}>People at {companyName} worth reaching out to before applying</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {contacts.map(contact => (
          <div key={contact.id} style={{ border: '1px solid rgba(0,0,0,0.07)', borderRadius: 14, padding: 16, background: 'rgba(250,250,248,0.6)' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
              <div>
                <p style={{ fontSize: 14, fontWeight: 600, color: '#0B1220', marginBottom: 2 }}>{contact.name}</p>
                {contact.role && <p style={{ fontSize: 12, color: '#475569', marginBottom: 2 }}>{contact.role}</p>}
                {contact.university && <p style={{ fontSize: 12, color: '#94A3B8' }}>{contact.university}{contact.graduation_year ? ` · ${contact.graduation_year}` : ''}</p>}
              </div>
              {contact.linkedin_url && (
                <motion.a
                  href={contact.linkedin_url} target="_blank" rel="noreferrer"
                  whileHover={{ scale: 1.04 }}
                  style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, color: '#0A66C2', background: '#EFF6FF', padding: '6px 12px', borderRadius: 8, flexShrink: 0 }}
                >
                  <Icons.linkedin /> LinkedIn
                </motion.a>
              )}
            </div>
            {!messages[contact.id] ? (
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => handleGenerateMessage(contact.id)}
                disabled={generatingFor === contact.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600,
                  background: 'var(--tandm-gradient-cta)', color: '#fff',
                  border: 'none', borderRadius: 10, padding: '9px 16px', cursor: 'pointer', fontFamily: 'var(--tandm-font-sans)',
                }}
              >
                {generatingFor === contact.id
                  ? <><div style={{ width: 13, height: 13, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} /> Writing...</>
                  : <><Icons.send /> Generate LinkedIn message</>
                }
              </motion.button>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ background: 'linear-gradient(135deg,#FFF8F4,#FFF4EE)', border: '1px solid #EDE0D6', borderRadius: 10, padding: '12px 14px', fontSize: 13, color: '#3D2E24', lineHeight: 1.65 }}>
                  {messages[contact.id]}
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <motion.button whileHover={{ scale: 1.03 }} onClick={() => handleGenerateMessage(contact.id)} style={{ fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 8, cursor: 'pointer', background: 'rgba(0,0,0,0.06)', color: '#475569', border: 'none', fontFamily: 'var(--tandm-font-sans)' }}>Regenerate</motion.button>
                  <motion.button whileHover={{ scale: 1.03 }} onClick={() => handleCopy(contact.id)} style={{ fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 8, cursor: 'pointer', background: 'var(--tandm-gradient-cta)', color: '#fff', border: 'none', fontFamily: 'var(--tandm-font-sans)' }}>
                    {copiedFor === contact.id ? '✓ Copied!' : 'Copy message'}
                  </motion.button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}

/* ─── Main ───────────────────────────────────────────────── */
export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [internship, setInternship] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applyOpen, setApplyOpen] = useState(false);

  useEffect(() => {
    api.get(`/internships/${id}`)
      .then(res => { setInternship(res.data.internship); })
      .catch(() => navigate('/jobs'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#F4F6F7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--tandm-font-sans)' }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
        <div style={{ width: 40, height: 40, border: '3px solid #EEF2F4', borderTopColor: '#4E6C7E', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <p style={{ fontSize: 13, color: '#94A3B8', fontWeight: 500 }}>Loading internship...</p>
      </div>
    </div>
  );
  if (!internship) return null;

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : 'Rolling';
  const catStyle = CAT_COLORS[internship.category] || CAT_COLORS['Other'];
    const mode = internship.apply_mode || 'manual';
  const modeStyle = applyModeStyle(mode);
  const atsName = portalLabel(internship.portal_type);
  const tierKey = resolveSupportTierKey(internship);
  const tierCompact = supportTierCompact(tierKey);
  const tierStyle = tierCompact ? supportTierStyle(tierCompact) : null;
  const coverage = certifiedCoverage(internship);
  const tierDisplayLabel = internship.apply_support_label
    || (tierCompact ? `${tierCompact}${atsName ? ` · ${atsName}` : ''}` : null);

const glanceItems = [
    { icon: Icons.location, label: 'Location',   value: internship.location },
    { icon: Icons.clock,    label: 'Duration',   value: internship.duration },
    { icon: Icons.calendar, label: 'Deadline',   value: formatDate(internship.deadline) },
    { icon: Icons.calendar, label: 'Start date', value: internship.start_date ? formatDate(internship.start_date) : null },
  ].filter(i => i.value);

  return (
    <div style={{ minHeight: '100vh', background: '#F4F6F7', fontFamily: 'var(--tandm-font-sans)' }}>
      <style>{`
        /* fonts via index.css tokens */
        @keyframes spin { to { transform: rotate(360deg) } }
        @keyframes gradientShift { 0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%} }
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-thumb { background: rgba(78,108,126,0.35); border-radius: 3px; }
        @media (max-width: 768px) { .jd-grid { grid-template-columns: 1fr !important; } .jd-sidebar { position: static !important; } }
      `}</style>

      <OfferDeskNav />

      {/* ── HERO HEADER ──────────────────────────────────── */}
      <div style={{
        background: 'linear-gradient(135deg,#0B1220 0%,#162033 60%,#0B1220 100%)',
        paddingTop: 'var(--tandm-nav-clearance, 112px)',
        paddingLeft: 24, paddingRight: 24, paddingBottom: 48,
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', inset: 0, opacity: 0.04, pointerEvents: 'none',
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.5) 1px,transparent 1px)',
          backgroundSize: '36px 36px',
        }} />
        <motion.div
          animate={{ scale: [1, 1.1, 1], opacity: [0.08, 0.13, 0.08] }}
          transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
          style={{ position: 'absolute', width: 350, height: 350, borderRadius: '50%', background: 'radial-gradient(circle,#6C969C 0%,transparent 70%)', right: -60, top: -80, pointerEvents: 'none' }}
        />
        <div style={{ maxWidth: 1040, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }}>
            <Link to="/jobs" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'rgba(255,255,255,0.45)', textDecoration: 'none', marginBottom: 28, fontWeight: 500, transition: 'color 0.2s' }}
              onMouseEnter={e => e.target.style.color = '#fff'}
              onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.45)'}
            >
              <Icons.arrowLeft /> Back to internships
            </Link>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
              <div style={{
                width: 64, height: 64, borderRadius: 18,
                background: `linear-gradient(135deg,${catStyle.bg},${catStyle.color}22)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 26, fontWeight: 800, color: catStyle.color, flexShrink: 0,
                border: `2px solid ${catStyle.color}30`,
              }}>
                {internship.company_name?.charAt(0)}
              </div>
              <div>
                <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', margin: '0 0 4px', fontWeight: 500 }}>{internship.company_name}</p>
                <h1 style={{ fontSize: 'clamp(20px,3vw,30px)', fontWeight: 800, color: '#fff', margin: 0, letterSpacing: '-0.03em', lineHeight: 1.2 }}>{internship.title}</h1>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 20 }}>
              <span style={{ fontSize: 11, fontWeight: 600, padding: '5px 13px', borderRadius: 100, background: catStyle.bg, color: catStyle.color }}>{internship.category}</span>
              <span style={{ fontSize: 11, fontWeight: 600, padding: '5px 13px', borderRadius: 100, background: modeStyle.bg, color: modeStyle.color }}>{applyModeLabel(mode)}</span>
              {atsName && (
                <span style={{ fontSize: 11, fontWeight: 700, padding: '5px 13px', borderRadius: 100, background: 'var(--tandm-accent-subtle)', color: 'var(--tandm-accent-deep)' }}>{atsName}</span>
              )}
              {tierCompact && (
                <span style={{ fontSize: 11, fontWeight: 700, padding: '5px 13px', borderRadius: 100, background: tierStyle.bg, color: tierStyle.color, border: `1px solid ${tierStyle.border}` }}>{tierCompact}</span>
              )}
              {internship.is_remote && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, padding: '5px 13px', borderRadius: 100, background: '#ECFDF5', color: '#065F46' }}>
                  <Icons.remote /> Remote
                </span>
              )}
              {internship.open_to_internationals && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, padding: '5px 13px', borderRadius: 100, background: '#EEF2FF', color: '#4338CA' }}>
                  <Icons.international /> Internationals welcome
                </span>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      {/* ── BODY ─────────────────────────────────────────── */}
      <div style={{ maxWidth: 1040, margin: '0 auto', padding: '32px 24px 60px' }}>
        <div className="jd-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 22, alignItems: 'start' }}>

          {/* ── LEFT ─────────────────────────────────────── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* At a glance */}
            <Card delay={0}>
              <h2 style={{ fontSize: 14, fontWeight: 700, color: '#0B1220', margin: '0 0 18px', letterSpacing: '-0.02em' }}>At a glance</h2>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
                {glanceItems.map(item => {
                  const Ic = item.icon;
                  return (
                    <div key={item.label} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                      <div style={{ marginTop: 2 }}><Ic /></div>
                      <div>
                        <p style={{ fontSize: 10, color: '#94A3B8', margin: '0 0 3px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{item.label}</p>
                        <p style={{ fontSize: 13, fontWeight: 600, color: '#0B1220', margin: 0 }}>{item.value}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>

            <AINotesPanel notes={internship.ai_notes} />
            <CoverLetterGenerator internship={internship} />


            <NetworkingContacts companyId={internship.company_id} companyName={internship.company_name} />
          </div>

          {/* ── SIDEBAR ──────────────────────────────────── */}
          <div className="jd-sidebar" style={{ display: 'flex', flexDirection: 'column', gap: 14, position: 'sticky', top: 'var(--tandm-nav-sticky-top)' }}>

            {/* Apply + Save */}
            <FadeIn delay={0.05}>
              <motion.div
                whileHover={{ y: -2 }}
                style={{
                  background: 'rgba(255,255,255,0.78)', backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.95)',
                  borderRadius: 20, padding: '20px', boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
                  display: 'flex', flexDirection: 'column', gap: 10,
                }}
              >
                {internship.application_url ? (
                  <motion.button
                    onClick={() => {
                      if (!user) {
                        navigate('/login');
                        return;
                      }
                      setApplyOpen(true);
                    }}
                    whileHover={{ scale: 1.02, boxShadow: '0 8px 28px rgba(78,108,126,0.35)' }}
                    whileTap={{ scale: 0.97 }}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      background: 'var(--tandm-gradient-cta)', color: '#fff',
                      borderRadius: 12, padding: '13px', fontSize: 14, fontWeight: 700,
                      textDecoration: 'none', boxShadow: '0 4px 16px rgba(78,108,126,0.25)',
                      border: 'none', cursor: 'pointer', width: '100%',
                      letterSpacing: '-0.01em',
                    }}
                  >
                    {internship.apply_mode === 'one_click' ? 'Apply with Tandm' : 'Start fast apply'} <Icons.arrowUpRight />
                  </motion.button>
                ) : (
                  <div style={{ textAlign: 'center', padding: '8px 0' }}>
                    <p style={{ fontSize: 13, color: '#94A3B8', marginBottom: 4 }}>No application link yet</p>
                    <p style={{ fontSize: 12, color: '#94A3B8' }}>Check the company website directly</p>
                  </div>
                )}
                {internship.apply_reason && (
                  <p style={{ margin: '0 0 4px', fontSize: 12, color: '#64748B', lineHeight: 1.55 }}>{internship.apply_reason}</p>
                )}
                {internship.portal_type === 'taleo' && user?.portal_account_password && (
                  <div style={{ padding: '10px 12px', borderRadius: 10, background: '#EEF2F4', border: '1px solid #D9E2E6', fontSize: 12 }}>
                    <p style={{ margin: '0 0 6px', fontWeight: 700, color: '#162033' }}>Portal login required</p>
                    <p style={{ margin: '0 0 8px', color: '#475569', lineHeight: 1.55 }}>Tandm will register you automatically. Your portal password:</p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <code style={{ flex: 1, padding: '6px 10px', background: '#fff', borderRadius: 6, fontFamily: 'monospace', fontSize: 13, fontWeight: 700, color: '#162033', letterSpacing: '0.04em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {user.portal_account_password}
                      </code>
                      <button
                        type="button"
                        onClick={() => navigator.clipboard?.writeText(user.portal_account_password)}
                        style={{ background: '#4E6C7E', border: 'none', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', color: '#fff', fontSize: 11, fontWeight: 700, fontFamily: 'var(--tandm-font-sans)', whiteSpace: 'nowrap' }}
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                )}
                {(atsName || tierCompact || coverage != null) && (
                  <div style={{
                    padding: '11px 12px', borderRadius: 10, background: 'var(--tandm-surface)',
                    border: '1px solid var(--tandm-line)', fontSize: 12,
                    color: 'var(--tandm-ink-soft)', lineHeight: 1.55,
                    display: 'flex', flexDirection: 'column', gap: 8,
                  }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {atsName && (
                        <span style={{
                          fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 8,
                          background: 'var(--tandm-accent-subtle)', color: 'var(--tandm-accent-deep)',
                        }}>{atsName}</span>
                      )}
                      <span style={{
                        fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 8,
                        background: modeStyle.bg, color: modeStyle.color,
                      }}>{applyModeLabel(mode)}</span>
                      {tierCompact && (
                        <span style={{
                          fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 8,
                          background: tierStyle.bg, color: tierStyle.color,
                          border: `1px solid ${tierStyle.border}`,
                        }}>{tierCompact}</span>
                      )}
                    </div>
                    {tierDisplayLabel && (
                      <div style={{ fontSize: 12, color: 'var(--tandm-body)' }}>
                        <strong style={{ color: 'var(--tandm-ink)' }}>{tierDisplayLabel}</strong>
                        {coverage != null ? ` · ${coverage}% schema coverage` : ''}
                      </div>
                    )}
                    {!tierDisplayLabel && coverage != null && (
                      <div style={{ fontSize: 12, color: 'var(--tandm-body)' }}>{coverage}% schema coverage</div>
                    )}
                  </div>
                )}
                {internship.pending_questions?.length > 0 && (
                  <div style={{
                    padding: '9px 12px', borderRadius: 10, background: '#E8F1F3',
                    border: '1px solid #D9E2E6', fontSize: 12, color: '#3A5468', lineHeight: 1.5,
                  }}>
                    <strong>{internship.pending_questions.length} {internship.pending_questions.length === 1 ? 'question' : 'questions'}</strong> for this role — you'll answer {internship.pending_questions.length === 1 ? 'it' : 'them'} in the apply flow.
                  </div>
                )}
                <SaveButton internshipId={internship.id} />
              </motion.div>
            </FadeIn>

            {/* How to apply */}
            <Card delay={0.1}>
              <h3 style={{ fontSize: 13, fontWeight: 700, color: '#0B1220', margin: '0 0 16px', letterSpacing: '-0.01em' }}>How to apply with Tandm</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {[
                  'Upload your CV and complete the profile essentials',
                  'Review the application packet Tandm prepares for this role',
                  internship.apply_mode === 'one_click' ? 'Tandm submits after your final confirmation' : 'Tandm fills in your browser — you review and submit',
                  'Track the application state from your dashboard',
                ].map((step, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    {Icons.steps(i + 1)}
                    <p style={{ fontSize: 12, color: '#475569', margin: 0, lineHeight: 1.6 }}>{step}</p>
                  </div>
                ))}
              </div>
            </Card>

          </div>
        </div>
      </div>
      <ApplyFlowModal
        open={applyOpen}
        internship={internship}
        onClose={() => setApplyOpen(false)}
        onApplicationUpdated={() => {}}
      />
    </div>
  );
}
